/**
 * Milestone Upgrade Prompt
 * Triggers when user achieves milestones (commitment & consistency principle)
 * Users are more receptive to upgrading when they feel invested
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Trophy, Sparkles, TrendingUp, Target, Calendar, Heart } from 'lucide-react';
import { MotionDiv } from '../lib/motion-utils';
import confetti from 'canvas-confetti';

interface MilestoneUpgradePromptProps {
  milestone: '7-day' | '14-day' | '30-day' | 'low-tension' | 'improvement' | 'consistent';
  data?: {
    streakDays?: number;
    tensionScore?: number;
    improvement?: number;
  };
}

export function MilestoneUpgradePrompt({ milestone, data }: MilestoneUpgradePromptProps) {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [hasShownConfetti, setHasShownConfetti] = useState(false);
  const [revealStep, setRevealStep] = useState(0);
  const [selectedBenefitIdx, setSelectedBenefitIdx] = useState(0);
  const [selectedChallenge, setSelectedChallenge] = useState<string | null>(null);

  useEffect(() => {
    // Check if this milestone celebration was already shown
    const shown = localStorage.getItem(`tension_milestone_${milestone}`);
    if (shown) {
      setIsVisible(false);
      return;
    }

    // Show confetti on mount
    if (!hasShownConfetti) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#8b5cf6', '#3b82f6', '#10b981']
      });
      setHasShownConfetti(true);
    }
  }, [milestone, hasShownConfetti]);

  const handleDismiss = () => {
    localStorage.setItem(`tension_milestone_${milestone}`, 'true');
    setIsVisible(false);
  };

  const handleUpgrade = () => {
    localStorage.setItem(`tension_milestone_${milestone}`, 'true');
    localStorage.setItem('tension_upgrade_from_milestone', milestone);
    navigate('/pricing');
  };

  if (!isVisible) return null;

  const getMilestoneConfig = () => {
    switch (milestone) {
      case '7-day':
        return {
          icon: <Calendar className="w-8 h-8 text-green-600" />,
          title: "🎉 7-Day Streak!",
          achievement: "You've completed a full week of tracking",
          insight: "You now have enough data to unlock advanced features. Premium users with 7+ days of data see 3× more accurate pattern detection and forecasting.",
          nextLevel: "What you'll unlock:",
          benefits: [
            "72-hour tension forecasting (72% accuracy)",
            "Behavioral pattern detection from your 7 days",
            "Personalized action cards based on your data",
            "Week-over-week trend analysis"
          ],
          cta: "Level up your insights",
          stat: `${data?.streakDays || 7} consecutive days tracked`,
          urgency: "Limited offer: First week subscribers get 20% off"
        };
      
      case '14-day':
        return {
          icon: <TrendingUp className="w-8 h-8 text-blue-600" />,
          title: "🚀 2-Week Milestone!",
          achievement: "You've built a solid tracking habit",
          insight: "With 14 days of data, our ML models can now detect deep patterns. Premium users at this stage unlock advanced forecasting and attachment analysis.",
          nextLevel: "Ready for deeper analysis:",
          benefits: [
            "14-day pattern analysis with ML insights",
            "Attachment style detection (94% accuracy)",
            "Advanced forecasting with weekly trends",
            "Behavior triggers identification"
          ],
          cta: "Unlock advanced insights",
          stat: `${data?.streakDays || 14} days of commitment`,
          urgency: "Your patterns are ready to be analyzed"
        };
      
      case '30-day':
        return {
          icon: <Trophy className="w-8 h-8 text-amber-600" />,
          title: "👑 Monthly Master!",
          achievement: "30 days of consistent tracking—you're in the top 5%",
          insight: "You've built one of the most valuable relationship datasets possible. Premium users at 30+ days see transformative insights and long-term stability.",
          nextLevel: "Unlock your full potential:",
          benefits: [
            "Monthly deep-dive reports with long-term trends",
            "Multi-relationship comparison (if applicable)",
            "Custom tension thresholds based on your baseline",
            "Predictive alerts before tension spikes"
          ],
          cta: "Access premium analysis",
          stat: "Top 5% of users",
          urgency: "Exclusive: 30-day users get priority support"
        };
      
      case 'low-tension':
        return {
          icon: <Heart className="w-8 h-8 text-pink-600" />,
          title: "💚 Healthy Tension!",
          achievement: `RTI Score: ${data?.tensionScore || 25}/100 - That's excellent`,
          insight: "You're in a good place. Premium helps you maintain this stability and prevent future spikes before they happen.",
          nextLevel: "Stay in the green zone:",
          benefits: [
            "Early warning alerts (detect spikes 48hrs early)",
            "Stability action cards to maintain low tension",
            "Pattern monitoring to prevent old habits",
            "Monthly stability reports"
          ],
          cta: "Protect your progress",
          stat: "Lower than 78% of relationships",
          urgency: "Prevention is easier than repair"
        };
      
      case 'improvement':
        return {
          icon: <Target className="w-8 h-8 text-violet-600" />,
          title: "📉 Major Improvement!",
          achievement: `Tension dropped ${data?.improvement || 40} points - incredible progress`,
          insight: "You're clearly doing something right. Premium users can identify exactly what's working and replicate it consistently.",
          nextLevel: "Understand what's working:",
          benefits: [
            "Improvement attribution analysis",
            "Success pattern identification",
            "Optimization recommendations",
            "Relapse prevention strategies"
          ],
          cta: "Keep the momentum",
          stat: `${data?.improvement || 40} point improvement`,
          urgency: "Lock in your success with data-backed strategies"
        };
      
      case 'consistent':
        return {
          icon: <Sparkles className="w-8 h-8 text-blue-600" />,
          title: "✨ Consistency Champion!",
          achievement: "You've checked in daily for 5+ days straight",
          insight: "Consistency is the key to relationship awareness. Premium users with daily check-ins see patterns 4× faster and make better interventions.",
          nextLevel: "Maximize your consistency:",
          benefits: [
            "Daily insights and micro-adjustments",
            "Streak protection with reminders",
            "Consistency rewards and milestones",
            "Daily action cards matched to your pattern"
          ],
          cta: "Unlock daily insights",
          stat: "Daily check-in streak active",
          urgency: "Daily users see results 4× faster"
        };
    }
  };

  const config = getMilestoneConfig();
  const challenges = [
    '3-day calm check-in streak',
    'Run one forecast review tonight',
    'Try one de-escalation play',
  ];
  const maxRevealStep = 3;
  const revealProgressPct = Math.round((revealStep / maxRevealStep) * 100);
  const visibleBenefits = config.benefits.slice(0, Math.max(1, revealStep + 1));

  return (
    <MotionDiv
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={handleDismiss}
    >
      <MotionDiv
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <Card className="p-8 bg-gradient-to-br from-white to-neutral-50 dark:from-neutral-900 dark:to-neutral-800 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-violet-500/20 to-blue-500/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-pink-500/20 to-amber-500/20 blur-3xl" />
          
          <div className="relative space-y-6">
            {/* Icon & Title */}
            <div className="text-center space-y-3">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-violet-100 to-blue-100 dark:from-violet-900/30 dark:to-blue-900/30 rounded-full flex items-center justify-center">
                {config.icon}
              </div>
              
              <div>
                <h2 className="text-2xl font-medium text-neutral-900 dark:text-neutral-100">
                  {config.title}
                </h2>
                <p className="text-neutral-600 dark:text-neutral-400 mt-1">
                  {config.achievement}
                </p>
              </div>
              
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 dark:bg-green-900/30 rounded-full">
                <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse" />
                <span className="text-xs font-medium text-green-800 dark:text-green-300">
                  {config.stat}
                </span>
              </div>
            </div>

            {/* Insight */}
            <div className="bg-neutral-100 dark:bg-neutral-800 rounded-lg p-4">
              <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
                {config.insight}
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                {config.nextLevel}
              </p>
              <div className="space-y-2">
                {visibleBenefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <Sparkles className={`w-4 h-4 flex-shrink-0 mt-0.5 ${selectedBenefitIdx === idx ? 'text-amber-500' : 'text-violet-600'}`} />
                    <span className="text-sm text-neutral-700 dark:text-neutral-300">
                      {benefit}
                    </span>
                    <button
                      type="button"
                      className="ml-auto text-[10px] text-violet-600 hover:text-violet-700 dark:text-violet-400"
                      onClick={() => setSelectedBenefitIdx(idx)}
                    >
                      highlight
                    </button>
                  </div>
                ))}
              </div>
              <div className="pt-1 flex items-center gap-2">
                <div className="h-1.5 flex-1 rounded-full bg-neutral-200 dark:bg-neutral-700 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-violet-500 to-blue-500 transition-all duration-300"
                    style={{ width: `${Math.min(100, revealProgressPct)}%` }}
                  />
                </div>
                {revealStep < maxRevealStep ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRevealStep((step) => Math.min(maxRevealStep, step + 1))}
                    className="h-7 px-2 text-xs"
                  >
                    Reveal more
                  </Button>
                ) : (
                  <span className="text-[11px] text-emerald-600 dark:text-emerald-400">All perks unlocked</span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Pick a mini-challenge (fun mode):</p>
              <div className="flex flex-wrap gap-2">
                {challenges.map((challenge) => (
                  <button
                    key={challenge}
                    type="button"
                    onClick={() => setSelectedChallenge(challenge)}
                    className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                      selectedChallenge === challenge
                        ? 'border-violet-500 bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
                        : 'border-neutral-300 bg-white text-neutral-700 hover:border-violet-400 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300'
                    }`}
                  >
                    {challenge}
                  </button>
                ))}
              </div>
            </div>

            {/* Urgency/Scarcity */}
            <div className="bg-gradient-to-r from-violet-50 to-blue-50 dark:from-violet-950/30 dark:to-blue-950/30 border border-violet-200 dark:border-violet-800 rounded-lg p-3 text-center">
              <p className="text-xs text-violet-900 dark:text-violet-300 font-medium">
                {config.urgency}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleDismiss}
                variant="outline"
                className="flex-1"
              >
                Maybe Later
              </Button>
              <Button
                onClick={handleUpgrade}
                className="flex-1 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700"
              >
                {selectedChallenge ? `Start: ${selectedChallenge}` : config.cta}
              </Button>
            </div>

            <p className="text-xs text-center text-neutral-500 dark:text-neutral-400">
              Start from $11.99/mo • Cancel anytime • 30-day guarantee
            </p>
          </div>
        </Card>
      </MotionDiv>
    </MotionDiv>
  );
}